import { routes } from 'api/routes';
import {
  AccountSettings,
  AppUser,
  Client,
  CreateClientInput,
  CreateProposalRequest,
  CreateProposalResponse,
  GenerateProposalResponse,
  GenerateProposalRequest,
  GenerateProposalSectionRequest,
  GenerateProposalSectionResponse,
  GetProposalResponse,
  InviteUserInput,
  ListClientsResponse,
  ListProposalsResponse,
  ListTemplatesResponse,
  ProposalSectionTypesResponse,
  PresignProposalAssetRequest,
  PresignProposalAssetResponse,
  PrepareSendResponse,
  RewriteProposalBlockRequest,
  RewriteProposalBlockResponse,
  SendProposalRequest,
  SendProposalResponse,
  ResearchClientInput,
  ResearchClientResponse,
  UpdateProposalRequest,
  UpdateProposalResponse,
  UpdateClientInput,
  UpdateAccountSettingsInput,
  User,
  PublicProposalResponse,
  PublicSignResponse,
  VisitorClaimProposalRequest,
  VisitorClaimProposalResponse,
  VisitorGenerateProposalRequest,
  VisitorGenerateProposalResponse,
  VisitorGenerateProposalSectionRequest,
  VisitorGenerateProposalSectionResponse,
  VisitorProposalResponse,
  VisitorProposalSectionTypesResponse,
  VisitorRewriteProposalBlockRequest,
  VisitorRewriteProposalBlockResponse,
  VisitorUpdateProposalRequest,
  VisitorUpdateProposalResponse,
} from 'api/types';
import { apiFetch } from 'api/api';

export const accountApi = {
  async fetchSettings(): Promise<AccountSettings> {
    return apiFetch(routes.account.settings, { method: 'GET' });
  },

  async updateSettings(input: UpdateAccountSettingsInput): Promise<AccountSettings> {
    return apiFetch(routes.account.settings, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
};

export const authApi = {
  /**
   * Fetches the application-level user record for the currently
   * authenticated Cognito user.
   *
   * GET /auth/me
   */
  async fetchCurrentUser(): Promise<AppUser> {
    return apiFetch(routes.auth.me, { method: 'GET' });
  },
};

export const usersApi = {
  /**
   * Invites a new user to the tenant.
   *
   * POST /tenants/{tenantId}/users
   */
  async inviteUser(tenantId: string, input: InviteUserInput): Promise<User> {
    return apiFetch(routes.users.invite(tenantId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Lists all users for a tenant.
   *
   * GET /tenants/{tenantId}/users
   */
  async listUsers(tenantId: string): Promise<User[]> {
    return apiFetch(routes.users.list(tenantId), {
      method: 'GET',
    });
  },
};

export const clientsApi = {
  /**
   * Lists clients for a tenant.
   *
   * GET /tenants/{tenantId}/clients
   */
  async listClients(
    tenantId: string,
    options?: {
      limit?: number;
      nextToken?: string;
    }
  ): Promise<ListClientsResponse> {
    const query = buildQuery({
      limit: options?.limit,
      nextToken: options?.nextToken,
    });

    return apiFetch(`${routes.clients.list(tenantId)}${query}`, { method: 'GET' });
  },

  /**
   * Creates a new client.
   *
   * POST /tenants/{tenantId}/clients
   */
  async createClient(tenantId: string, input: CreateClientInput): Promise<Client> {
    return apiFetch(routes.clients.create(tenantId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Fetches a single client by id.
   *
   * GET /tenants/{tenantId}/clients/{clientId}
   */
  async getClient(tenantId: string, clientId: string): Promise<Client> {
    return apiFetch(routes.clients.get(tenantId, clientId), {
      method: 'GET',
    });
  },

  /**
   * Updates an existing client.
   *
   * PATCH /tenants/{tenantId}/clients/{clientId}
   */
  async updateClient(tenantId: string, clientId: string, input: UpdateClientInput): Promise<Client> {
    return apiFetch(routes.clients.update(tenantId, clientId), {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  /**
   * Deletes a client.
   *
   * DELETE /tenants/{tenantId}/clients/{clientId}
   */
  async deleteClient(tenantId: string, clientId: string): Promise<void> {
    await apiFetch(routes.clients.delete(tenantId, clientId), { method: 'DELETE' });
  },

  /**
   * Researches a client and returns suggested description/logo.
   *
   * POST /tenants/{tenantId}/clients/research
   */
  async researchClient(tenantId: string, input: ResearchClientInput): Promise<ResearchClientResponse> {
    return apiFetch(routes.clients.research(tenantId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

export const proposalsApi = {
  /**
   * Creates a new proposal for a client.
   *
   * POST /tenants/{tenantId}/proposals
   */
  async createProposal(
    tenantId: string,
    clientId: string,
    templateId: string,
    themeId?: string
  ): Promise<CreateProposalResponse> {
    const payload: CreateProposalRequest = { clientId, templateId, ...(themeId ? { themeId } : {}) };
    return apiFetch(routes.proposals.create(tenantId), {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Fetches a single proposal.
   *
   * GET /tenants/{tenantId}/proposals/{proposalId}
   */
  async getProposal(tenantId: string, proposalId: string): Promise<GetProposalResponse> {
    return apiFetch(routes.proposals.get(tenantId, proposalId), {
      method: 'GET',
    });
  },

  /**
   * Updates proposal document metadata.
   *
   * PUT /tenants/{tenantId}/proposals/{proposalId}
   */
  async updateProposal(
    tenantId: string,
    proposalId: string,
    input: UpdateProposalRequest
  ): Promise<UpdateProposalResponse> {
    return apiFetch(routes.proposals.update(tenantId, proposalId), {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Hard deletes a proposal and its canonical document.
   *
   * DELETE /tenants/{tenantId}/proposals/{proposalId}
   */
  async deleteProposal(tenantId: string, proposalId: string): Promise<void> {
    await apiFetch(routes.proposals.delete(tenantId, proposalId), {
      method: 'DELETE',
    });
  },

  /**
   * Generates proposal content via OpenAI.
   *
   * POST /tenants/{tenantId}/proposals/{proposalId}/generate
   */
  async generateProposal(
    tenantId: string,
    proposalId: string,
    input?: GenerateProposalRequest
  ): Promise<GenerateProposalResponse> {
    return apiFetch(routes.proposals.generate(tenantId, proposalId), {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    });
  },

  /**
   * Generates and inserts a single section into the canonical proposal document.
   *
   * POST /tenants/{tenantId}/proposals/{proposalId}/sections/generate
   */
  async generateProposalSection(
    tenantId: string,
    proposalId: string,
    input: GenerateProposalSectionRequest
  ): Promise<GenerateProposalSectionResponse> {
    return apiFetch(routes.proposals.generateSection(tenantId, proposalId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Prepares a proposal for sending (public link).
   *
   * POST /tenants/{tenantId}/proposals/{proposalId}/prepare-send
   */
  async prepareSend(tenantId: string, proposalId: string): Promise<PrepareSendResponse> {
    return apiFetch(routes.proposals.prepareSend(tenantId, proposalId), {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  /**
   * Sends a proposal email.
   *
   * POST /tenants/{tenantId}/proposals/{proposalId}/send
   */
  async sendProposal(tenantId: string, proposalId: string, input: SendProposalRequest): Promise<SendProposalResponse> {
    return apiFetch(routes.proposals.send(tenantId, proposalId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Lists proposals for a tenant.
   *
   * GET /tenants/{tenantId}/proposals
   */
  async listProposalsByTenant(
    tenantId: string,
    options?: { limit?: number; status?: string; nextToken?: string }
  ): Promise<ListProposalsResponse> {
    // Keep options flexible for future server-side filtering/pagination.
    const query = buildQuery({
      limit: options?.limit,
      status: options?.status,
      nextToken: options?.nextToken,
    });

    return apiFetch(`${routes.proposals.list(tenantId)}${query}`, { method: 'GET' });
  },

  /**
   * Lists proposals for a specific client (tenant-scoped).
   *
   * GET /tenants/{tenantId}/clients/{clientId}/proposals
   */
  async listProposalsByClient(
    tenantId: string,
    clientId: string,
    options?: { limit?: number; status?: string; nextToken?: string }
  ): Promise<ListProposalsResponse> {
    // Keep options flexible for future server-side filtering/pagination.
    const query = buildQuery({
      limit: options?.limit,
      status: options?.status,
      nextToken: options?.nextToken,
    });

    return apiFetch(`${routes.proposals.listByClient(tenantId, clientId)}${query}`, { method: 'GET' });
  },

  /**
   * Lists canonical proposal section types.
   *
   * GET /section-types
   */
  async listProposalSectionTypes(): Promise<ProposalSectionTypesResponse> {
    return apiFetch(routes.proposalSectionTypes.list(), { method: 'GET' });
  },

  /**
   * Creates a presigned upload URL for proposal assets.
   *
   * POST /tenants/{tenantId}/proposals/{proposalId}/assets/presign
   */
  async presignAsset(
    tenantId: string,
    proposalId: string,
    input: PresignProposalAssetRequest
  ): Promise<PresignProposalAssetResponse> {
    return apiFetch(routes.proposals.presignAsset(tenantId, proposalId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Rewrites a single text block with AI (non-mutating).
   *
   * POST /tenants/{tenantId}/proposals/{proposalId}/blocks/{blockId}/rewrite
   */
  async rewriteProposalBlock(
    tenantId: string,
    proposalId: string,
    blockId: string,
    input: RewriteProposalBlockRequest
  ): Promise<RewriteProposalBlockResponse> {
    return apiFetch(routes.proposals.rewriteBlock(tenantId, proposalId, blockId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

export const templatesApi = {
  /**
   * Lists templates for a tenant.
   *
   * GET /tenants/{tenantId}/templates
   */
  async listTemplates(tenantId: string): Promise<ListTemplatesResponse> {
    return apiFetch(routes.templates.list(tenantId), { method: 'GET' });
  },
};

export const publicApi = {
  /**
   * Fetches a public proposal snapshot by token.
   *
   * GET /public/proposal/{token}
   */
  async getPublicProposal(token: string): Promise<PublicProposalResponse> {
    return apiFetch(routes.public.getProposal(token), { method: 'GET' });
  },

  /**
   * Signs a public proposal.
   *
   * POST /public/proposal/{token}/sign
   */
  async signPublicProposal(token: string, name: string): Promise<PublicSignResponse> {
    return apiFetch(routes.public.signProposal(token), {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },
};

export const visitorApi = {
  async listSectionTypes(): Promise<VisitorProposalSectionTypesResponse> {
    return apiFetch(routes.visitor.listSectionTypes(), { method: 'GET' });
  },

  async generateProposal(input: VisitorGenerateProposalRequest): Promise<VisitorGenerateProposalResponse> {
    return apiFetch(routes.visitor.generateProposal(), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async getProposal(visitorProposalId: string, previewToken: string): Promise<VisitorProposalResponse> {
    const query = buildQuery({ previewToken });
    return apiFetch(`${routes.visitor.getProposal(visitorProposalId)}${query}`, {
      method: 'GET',
    });
  },

  async updateProposal(
    visitorProposalId: string,
    input: VisitorUpdateProposalRequest
  ): Promise<VisitorUpdateProposalResponse> {
    return apiFetch(routes.visitor.updateProposal(visitorProposalId), {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  async generateProposalSection(
    visitorProposalId: string,
    input: VisitorGenerateProposalSectionRequest
  ): Promise<VisitorGenerateProposalSectionResponse> {
    return apiFetch(routes.visitor.generateSection(visitorProposalId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async rewriteProposalBlock(
    visitorProposalId: string,
    blockId: string,
    input: VisitorRewriteProposalBlockRequest
  ): Promise<VisitorRewriteProposalBlockResponse> {
    return apiFetch(routes.visitor.rewriteBlock(visitorProposalId, blockId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  async claimProposal(
    visitorProposalId: string,
    input: VisitorClaimProposalRequest
  ): Promise<VisitorClaimProposalResponse> {
    return apiFetch(routes.visitor.claimProposal(visitorProposalId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

function buildQuery(params: Record<string, string | number | undefined>) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) qs.set(key, String(value));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}
